import { observer } from "mobx-react-lite";

import { DateTime, MonthYearRecurrence, TIME_FORMATS } from "@bps/date-time";
import { FontIcon, Heading, Stack, Text, TooltipHost } from "@bps/fluent-ui";
import {
  Frequency,
  ScheduleDto
} from "@libs/gateways/booking/BookingGateway.dtos.ts";
import { getRecurrenceNaturalText } from "@modules/booking/screens/booking-calendar/components/appointment-dialog/components/appointment-form/utils.ts";
import { UserAvailability } from "@stores/booking/models/UserAvailability.ts";

interface BhbProviderWorkingHoursTooltipProps {
  locationId: string;
  userAvailability: UserAvailability;
}

export const BhbProviderWorkingHoursTooltip = observer(
  ({ locationId, userAvailability }: BhbProviderWorkingHoursTooltipProps) => {
    const schedules = userAvailability.schedules.filter(
      schedule => schedule.orgUnitId === locationId && schedule.isAvailable
    );

    if (!schedules.length) return null;

    return (
      <TooltipHost
        content={
          <BhbProviderWorkingHoursTooltipContent schedules={schedules} />
        }
      >
        <FontIcon iconName="Info" />
      </TooltipHost>
    );
  }
);

// This is a separate component so that it can be easily tested
export const BhbProviderWorkingHoursTooltipContent = observer(
  ({ schedules }: { schedules: ScheduleDto[] }) => {
    return (
      <Stack
        tokens={{ childrenGap: 16 }}
        styles={{ root: { padding: "20px 24px" } }}
      >
        <Heading variant="modal-heading">Working hours</Heading>
        <Stack tokens={{ childrenGap: 16 }}>
          {schedules.map(schedule => {
            let monthYearRecurrence: MonthYearRecurrence | undefined;

            if (schedule.recurrenceRule.frequency !== Frequency.Week) {
              if ((schedule.recurrenceRule.weekPosition ?? 0) > 0) {
                monthYearRecurrence = MonthYearRecurrence.DayOfWeek;
              } else if ((schedule.recurrenceRule.weekPosition ?? 0) < 0) {
                monthYearRecurrence = MonthYearRecurrence.LastDayOfWeek;
              } else {
                monthYearRecurrence = MonthYearRecurrence.MonthDay;
              }
            }

            const rawText = getRecurrenceNaturalText({
              startDate: DateTime.jsDateFromISO(schedule.startDate),
              dayRecur: schedule.recurrenceRule.dayRecur,
              frequency: schedule.recurrenceRule.frequency,
              interval: schedule.recurrenceRule.interval,
              monthYearRecurrence
            });

            const recurrenceText = `${rawText[0].toUpperCase()}${rawText.slice(
              1
            )}`;

            return (
              <Stack key={schedule.id}>
                <Text>{`${DateTime.fromParsedTimeString(
                  schedule.startTime
                )?.toFormat(
                  TIME_FORMATS.DEFAULT_TIME_FORMAT
                )} to ${DateTime.fromParsedTimeString(
                  schedule.endTime
                )?.toFormat(TIME_FORMATS.DEFAULT_TIME_FORMAT)}`}</Text>
                <Text>{recurrenceText}</Text>
              </Stack>
            );
          })}
        </Stack>
      </Stack>
    );
  }
);
